import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../lib/api";
import {
  anonRef, getConsentChoice, isCustomerRoute, loadGA, pageView, setConsentChoice,
} from "../lib/analytics";

/** Mounted inside BrowserRouter (App.js). Three jobs:
 * 1. fetch the admin-configured GA Measurement ID (empty = GA fully absent, bar included);
 * 2. fire one manual page_view per route change — customer surfaces only;
 * 3. the one-line consent bar: first visit only, OK and "No thanks" equally easy,
 *    choice remembered so it never re-nags, never blocks ordering (non-modal strip).
 * Every choice writes a consent_log row via POST /api/consent (anonymous local ref). */
export default function AnalyticsBridge() {
  const location = useLocation();
  const [gaId, setGaId] = useState("");
  const [choice, setChoice] = useState(getConsentChoice());

  useEffect(() => {
    api.get("/settings/public")
      .then((r) => setGaId(r.data.ga_measurement_id || ""))
      .catch(() => {});
  }, []);

  useEffect(() => {
    // never even load the script on staff/BOH surfaces — kitchen tablets stay dark
    if (gaId && choice === "granted" && isCustomerRoute(location.pathname)) loadGA(gaId);
  }, [gaId, choice, location.pathname]);

  useEffect(() => {
    if (gaId && choice === "granted" && isCustomerRoute(location.pathname)) {
      pageView(location.pathname);
    }
  }, [location.pathname, gaId, choice]);

  // listen for a withdrawal from the Privacy page (same-tab storage writes don't event)
  useEffect(() => {
    const sync = () => setChoice(getConsentChoice());
    window.addEventListener("indg-analytics-consent-changed", sync);
    return () => window.removeEventListener("indg-analytics-consent-changed", sync);
  }, []);

  const decide = (granted) => {
    setConsentChoice(granted);
    setChoice(granted ? "granted" : "denied");
    api.post("/consent", {
      subject_ref: anonRef(), type: "ANALYTICS",
      action: granted ? "GRANTED" : "WITHDRAWN",
    }).catch(() => {});
  };

  if (!gaId || choice || !isCustomerRoute(location.pathname)) return null;
  return (
    <div className="consent-bar" role="region" aria-label="Analytics choice" data-testid="analytics-consent-bar">
      <span className="consent-bar-text">We use anonymous analytics to improve the app — OK?</span>
      <button className="consent-bar-ok" onClick={() => decide(true)} data-testid="analytics-consent-ok">OK</button>
      <button className="consent-bar-no" onClick={() => decide(false)} data-testid="analytics-consent-decline">No thanks</button>
    </div>
  );
}
